import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, User } from "lucide-react";
import { UserDetails } from "@/types/userDetails";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { commonMiscLabels } from "@/i18n";

interface ListingDetailsBuyerProps {
  buyer?: UserDetails; // User object (optional)
}

export const ListingDetailsBuyer: React.FC<ListingDetailsBuyerProps> = ({
  buyer,
}) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};

  if (!buyer) {
    return <div>{L.noBuyerAvailable ?? "Ingen köpare tillgänglig"}</div>;
  }

  const isBuyerLoading = false;
  const buyerError = null;

  if (isBuyerLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">
          {L.loadingBuyerInfo ?? "Laddar köparinformation..."}
        </span>
      </div>
    );
  }

  if (buyerError) {
    const commonLabels = commonMiscLabels(language);
    return (
      <p className="text-muted-foreground">
        {commonLabels.error}: {buyerError.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Buyer Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {L.buyerInfo ?? "Köparinformation"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="font-medium text-green-500">{L.name ?? "Namn"}</p>
                <p className="text-sm">
                  {buyer.firstName} {buyer.lastName}
                </p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-green-500">
                  {L.email ?? "E-post"}
                </p>
                <p className="text-sm">{buyer.email}</p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-green-500">
                  {L.phone ?? "Telefon"}
                </p>
                <p className="text-sm">
                  {buyer.phoneNumber || (L.phoneNotProvided ?? "Ej angivet")}
                </p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-green-500">{L.type ?? "Typ"}</p>
                <Badge variant="default">
                  {buyer.role === "dealer"
                    ? (L.dealer ?? "Återförsäljare")
                    : buyer.role === "business"
                      ? (L.business ?? "Företagskund")
                      : (L.private ?? "Privatperson")}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
